import React from 'react';
import { styled } from '@mui/material/styles';
import {
    Autocomplete,
    Box,
    createFilterOptions,
    Popper,
    PopperProps,
    TextField,
} from '@mui/material';
import { INSCRIPTION_ID } from 'routes/BudgetModule/config/constants';
import { useUpdateOrphanInscription } from '../helpers/hooks/updateInscription';
import {
    InscriptionWithMetadata,
    BudgetProposalResourceMetadataT,
} from 'routes/BudgetModule/config/types';
import { InscriptionQueryKey } from 'routes/BudgetModule/helpers/createBudgetQueryKeys';

// ---------- Styled Components ----------
const StyAutocomplete = styled(Autocomplete, { name: 'StyAutocomplete' })({
    '& .MuiInputBase-root': {
        height: '32px',
    },
});

const StyBox = styled(Box, { name: 'StyBox' })({
    lineHeight: '36px',
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1,
    '& .MuiInputBase-root': {
        paddingBottom: 0,
        paddingTop: 0,
        height: '32px',
        '& .MuiInputBase-input': {
            fontSize: '12px',
        },
    },
});

// ---------- Custom Popper ----------
const CustomPopper = (props: PopperProps): React.ReactElement => (
    <Popper
        {...props}
        placement="bottom"
        modifiers={[
            {
                name: 'flip',
                enabled: true,
                options: {
                    fallbackPlacements: ['right-start', 'left-start'],
                },
            },
        ]}
    />
);

// ---------- Base Props Type ----------
export type BaseCellRendererProps<
    TOption extends Record<string, unknown>,
    TValue extends string | null,
> = {
    data: InscriptionWithMetadata;
    value: TValue;
    setValue: (val: TValue | null) => void;
    trackingInformation?: {
        eventName: string;
        metadata?: BudgetProposalResourceMetadataT;
    };
    queryCacheKey: InscriptionQueryKey;
    options: TOption[];
    noValue: TOption;
    optionLabelKey: keyof TOption;
    optionValueKey: keyof TOption;
    updateFieldKey: string;
};

// ---------- Component ----------
export default function BaseCellRenderer<
    TOption extends Record<string, unknown>,
    TValue extends string | null,
>({
    data,
    value,
    setValue,
    trackingInformation,
    queryCacheKey,
    options,
    noValue,
    optionLabelKey,
    optionValueKey,
    updateFieldKey,
}: BaseCellRendererProps<TOption, TValue>): React.ReactElement {
    const updateInscriptionMutation = useUpdateOrphanInscription(
        queryCacheKey,
        trackingInformation,
    );

    if (value === null || data === undefined) {
        return <span />;
    }

    const { inscription } = data;

    const currentValue: TOption =
        options.find((option) => option[optionValueKey] === value) || noValue;

    const filterOptions = createFilterOptions<TOption>({
        limit: 1000,
    });

    const allOptions: TOption[] = [noValue, ...options];

    return (
        <StyBox>
            <StyAutocomplete
                filterOptions={filterOptions}
                options={allOptions}
                value={currentValue}
                getOptionLabel={(option: TOption): string =>
                    String(option[optionLabelKey])
                }
                sx={{ width: '100%', height: '32px' }}
                PopperComponent={CustomPopper}
                onChange={(_, newValue: TOption | null): void => {
                    const softwareId = newValue?.[optionValueKey] ?? '';

                    updateInscriptionMutation.mutate({
                        id: inscription[INSCRIPTION_ID],
                        data: {
                            [updateFieldKey]: softwareId,
                        },
                    });

                    setValue(softwareId === '' ? null : (softwareId as TValue));
                }}
                renderInput={(params) => (
                    <TextField {...params} sx={{ flex: 1 }} size="small" />
                )}
            />
        </StyBox>
    );
}
